import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { cedulaValidator, rucValidator } from 'src/app/shared';
import HttpStatusCode from 'src/app/shared/enum/HttpStatusCode';
import { Signature } from 'src/app/shared/interfaces/signature';
import { SharedModule } from 'src/app/shared/shared.module';
import { SharedService } from 'src/app/shared/shared.service';
import { FormFieldClickableDirective } from 'src/app/shared/utils/form-field-clickable.directive';
import { FormValidator } from 'src/app/shared/utils/form-validator';
import {
  checkSignature,
  itsNotYourId,
  signatureExpired,
} from 'src/app/shared/utils/validator/signature.validator';

import { LoadingComponent } from '../../generals/loading/loading.component';
import { SignatureService } from '../../services/signature.service';
import { TaxIdSignatureType } from './tax-id-signature-type.enum';

@Component({
  selector: 'app-my-signature',
  templateUrl: './my-signature.component.html',
  styleUrls: ['./my-signature.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    SharedModule,
    MatTooltipModule,
    MatButtonModule,
    LoadingComponent,
    FormFieldClickableDirective,
  ],
})
export class MySignatureComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private fv = inject(FormValidator);
  private sharedService = inject(SharedService);
  private service = inject(SignatureService);
  private _unsubscribeAll!: Subject<any>;
  private _snackBar = inject(MatSnackBar);
  private taxIdType: number = TaxIdSignatureType.NONE;
  private previousFileValue = false;
  @ViewChild('formDirective')
  private formDirective!: FormGroupDirective;
  CIForm!: FormGroup;
  RUCForm!: FormGroup;
  file!: File;
  resultOf: any = {};
  ciSelectedFileName = '';
  rucSelectedFileName = '';
  get selectedFileName(): string {
    return this.taxIdType === TaxIdSignatureType.CI
      ? this.ciSelectedFileName
      : this.rucSelectedFileName;
  }
  CINotification: any = {};
  RUCNotification: any = {};
  isLoading = false;
  showPassword = false;
  panelOpenState = false;

  ngOnInit(): void {
    this._unsubscribeAll = new Subject();
    this.load();
  }

  private load() {
    this.service.getSignatureSimple().subscribe({
      next: response => {
        this.sharedService.setSignature(response.data);
        let esForm;
        if (response.data.taxIdType.id === TaxIdSignatureType.CI) {
          this.initializeForm(TaxIdSignatureType.CI);
          esForm = this.CIForm;
        } else {
          this.initializeForm(TaxIdSignatureType.RUC);
          esForm = this.RUCForm;
        }
        esForm.patchValue({ dueDate: response.data.dueDate });
        this.taxIdType = response.data.taxIdType.id;
      },
    });
  }

  save(): void {
    this.isLoading = true;
    const formData = this.getFormData();
    const form = this.getForm();
    const control = form['controls']['file'];
    this.service.save(formData).subscribe({
      next: response => {
        this.sharedService.setSignature(response.data);
        const dueDate = form.get('dueDate')?.value;
        form.reset();
        this.formDirective.resetForm();
        form.patchValue({ dueDate });
        this.isLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === HttpStatusCode.NOT_ACCEPTABLE) {
          if (err.error.data && err.error.data.dueDate) {
            form.patchValue({
              dueDate: err.error.data.dueDate,
            });
          } else {
            control.setErrors({ signatureNotCurrent: true });
            this.openSnack('Hay una firma vigente registrada');
          }
        }
        this.isLoading = false;
      },
    });
  }

  private getFormData(): FormData {
    const form = this.getForm();
    const dataJson = form.value as Signature;
    const data = JSON.stringify(dataJson);
    const formData = new FormData();
    formData.append('data', new Blob([data], { type: 'application/json' }));
    formData.append('file', this.file);
    return formData;
  }

  initializeForm(taxIdType: number): void {
    this.taxIdType = taxIdType;
    if (this.isNewSignature()) {
      this.initializeFormES();
      this.initializeFormValidationES();
    }
    this.validateFormStatus();
  }

  private initializeFormES(): void {
    const length = this.taxIdType === TaxIdSignatureType.CI ? 10 : 13;
    const formGroup = this.fb.group(
      {
        id: [
          '',
          {
            validators: [
              cedulaValidator,
              Validators.required,
              Validators.pattern('^[0-9]*$'),
              Validators.minLength(length),
              Validators.maxLength(length),
            ],
            asyncValidators: [itsNotYourId(this.service)],
            updateOn: 'blur',
          },
        ],
        file: ['', Validators.required],
        auxiliar: ['', Validators.required],
        password: ['', Validators.required],
        dueDate: [
          {
            value: '',
            disabled: true,
          },
          {
            validators: [signatureExpired],
            updateOn: 'change',
          },
        ],
      },
      {
        asyncValidators: [
          checkSignature(
            this.service,
            () => this.file,
            isLoading => (this.isLoading = isLoading),
          ),
        ],
        updateOn: 'blur',
      },
    );

    if (this.taxIdType === TaxIdSignatureType.CI) {
      this.CIForm = formGroup;
      return;
    }
    formGroup.get('id')?.addValidators(rucValidator);
    formGroup.get('id')?.updateValueAndValidity();
    this.RUCForm = formGroup;
  }

  private initializeFormValidationES(): void {
    const length = this.taxIdType === TaxIdSignatureType.CI ? 10 : 13;
    const idTypeText = this.taxIdType === TaxIdSignatureType.CI ? 'CI' : 'RUC';
    const validationMessage = {
      id: {
        required: `${idTypeText} es requerido`,
        cedulaNoValida: `${idTypeText} no valido`,
        rucValidator: `${idTypeText} no valido`,
        itsNotYourId: `${idTypeText} no corresponde al usuario`,
        minlength: `${idTypeText} debe tener ${length} digitos`,
        maxlength: `${idTypeText} debe tener ${length} digitos`,
      },
      password: {
        required: 'Contraseña requerida',
        badPassword: 'Contraseña incorrecta',
      },
      file: {
        required: 'Firma requerida',
        isNotOwned: 'La firma no es propiedad del usuario',
        conflict: 'La firma está dañada',
        signatureNotCurrent: 'La firma no es la más vigente',
        signatureExpired: 'La firma ha expirado',
      },
    };
    const formToValidation = this.getForm();
    const notification =
      this.taxIdType === TaxIdSignatureType.CI
        ? this.CINotification
        : this.RUCNotification;
    this.fv.setFormValidator(formToValidation, notification, validationMessage);
  }

  private isNewSignature(): boolean {
    const form = this.getForm();
    const dueDate: string = form?.get('dueDate')?.value;
    return !dueDate;
  }

  private getForm(): FormGroup {
    const form: FormGroup =
      this.taxIdType === TaxIdSignatureType.CI ? this.CIForm : this.RUCForm;
    return form;
  }

  private deleteError(control: AbstractControl, validator: string): void {
    if (control.hasError(validator)) {
      delete control.errors?.[validator];
    }
    control.updateValueAndValidity({ onlySelf: true });
  }

  controlHasError(formControlName: string): boolean {
    return this.fv.fieldHasError(formControlName);
  }

  handleFile(event: any): void {
    this.reset();
    const form = this.getForm();
    this.file = event.target.files.item(0);
    const auxiliar = form.get('auxiliar');
    let fileName = '';
    if (this.file) {
      fileName = this.file.name;
      form.get('file')?.setValue(this.file);
      auxiliar?.setErrors(null);
      auxiliar?.markAsPristine();
      auxiliar?.markAsUntouched();
    } else {
      auxiliar?.markAsTouched();
      auxiliar?.updateValueAndValidity({
        onlySelf: false,
      });
      form.get('file')?.setValue(null);
    }
    if (this.taxIdType === TaxIdSignatureType.CI) {
      this.ciSelectedFileName = fileName;
    } else if (this.taxIdType === TaxIdSignatureType.RUC) {
      this.rucSelectedFileName = fileName;
    }
  }

  clickFile(): void {
    const form = this.getForm();
    const control = form.get('file');
    this.previousFileValue = !!this.file;
  }

  blurFile() {
    const form = this.getForm();
    const control = form.get('file');
    const auxiliar = form.get('auxiliar');
    const stillEmpty = !this.file;
    if (!this.previousFileValue && stillEmpty) {
      control?.markAsTouched();
    }
    control?.updateValueAndValidity({
      onlySelf: false,
      emitEvent: true,
    });
  }

  reset(): void {
    const form = this.getForm();
    const notification =
      this.taxIdType === TaxIdSignatureType.CI
        ? this.CINotification
        : this.RUCNotification;
    notification.password = '';
    notification.dueDate = '';
    form['controls']['password'].reset();
    form['controls']['dueDate'].reset();

    if (this.taxIdType === TaxIdSignatureType.CI) {
      this.ciSelectedFileName = '';
    } else if (this.taxIdType === TaxIdSignatureType.RUC) {
      this.rucSelectedFileName = '';
    }
  }

  clearDueDate() {
    const form = this.getForm();
    const notification =
      this.taxIdType === TaxIdSignatureType.CI
        ? this.CINotification
        : this.RUCNotification;
    notification.dueDate = '';
    form['controls']['dueDate'].reset();
  }

  validateFormStatus() {
    const form = this.getForm();
    form.statusChanges.subscribe(status => {
      this.isLoading = (status === 'PENDING');
    });
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
      duration: 5000,
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(void 0);
    this._unsubscribeAll.complete();
  }
}
